import { useCallback, useMemo, useState } from 'react';

/**
 * DirtyMap - tracks multiple dirty states across different keys and provides utilities for management.
 * Used to aggregate dirty flags from multiple components (e.g., multiple forms in a panel).
 * Keys are only stored in the map when their value is true, keeping the structure lean.
 */
export interface DirtyMap {
  /** Record of dirty states keyed by identifier. Only contains true values. */
  map: Record<string, boolean>;
  /** Computed boolean indicating if any key has a dirty state (map has any keys). */
  isAnyDirty: boolean;
  /** Set the dirty state for a specific key. True adds the key to map, false removes it. */
  setDirty: (key: string, dirty: boolean) => void;
  /** Remove a specific key from the dirty map. */
  resetKey: (key: string) => void;
  /** Clear all keys from the dirty map. */
  resetAll: () => void;
}

/**
 * useDirtyMap - small helper to track multiple dirty flags and compute an aggregate boolean.
 * - Keys are added when `dirty === true` and removed when `dirty === false` to keep the map small.
 */
export const useDirtyMap = (initial?: Record<string, boolean>): DirtyMap => {
  const [map, setMap] = useState<Record<string, boolean>>(initial ?? {});

  const setDirty = useCallback((key: string, dirty: boolean) => {
    setMap((prev) => {
      const current = Boolean(prev[key]);
      if (current === dirty) return prev;

      const next = { ...prev };
      if (dirty) {
        next[key] = true;
      } else {
        delete next[key];
      }

      return next;
    });
  }, []);

  const resetKey = useCallback((key: string) => {
    setMap((prev) => {
      if (!(key in prev)) return prev;

      const next = { ...prev };
      delete next[key];

      return next;
    });
  }, []);

  const resetAll = useCallback(() => setMap({}), []);

  const isAnyDirty = useMemo(() => Object.keys(map).length > 0, [map]);

  return { map, isAnyDirty, setDirty, resetKey, resetAll };
};
